/**
 * 交叉表构建器：行维度（向下扩展）+ 列维度（向右扩展）+ 交叉取值单元格。
 * 结构（参考真实交叉样本）：
 *   - 角区：A1 合并（rowspan=行维度层级, colspan=列维度层级），静态标签
 *   - 列头：C1.. 向右扩展（expand=right），绑定列维度字段
 *   - 行头：A4.. 向下扩展（expand=down），绑定行维度字段
 *   - 取值：C4.. 交叉单元格，绑定度量字段（dataset + aggregate="sum"），横向并排；
 *     其 top 指向列头、left 指向行头
 *
 * 本构建器生成「单层级行维度 + 单层级列维度」的标准交叉表；多层级可在此基础上扩展。
 * 度量通过 measures 传入（单度量传单元素数组，多度量横向各占一列）。
 */

import type { ReportIR, CellIR, DatasetIR, DatasourceIR } from '../ir/types.js';

/** 维度字段描述（行/列维度共用）。 */
export type DimField = { name: string; label?: string; type: DatasetIR['fields'][number]['type'] };

/** 度量字段描述（取值格）。 */
export type MeasureField = { name: string; label?: string; type: DatasetIR['fields'][number]['type'] };

/** 角区斜线表头配置。 */
export interface CornerConfig {
  /** 斜线方向：down=左上到右下（缺省），right=左下到右上。 */
  direction?: 'down' | 'right';
}

export interface CrosstabInput {
  name?: string;
  datasetName: string;
  datasource: DatasourceIR;
  rowDim: DimField;
  colDim: DimField;
  /** 度量列表（取值格）。单度量传单元素数组即可；多个度量横向各占一列。 */
  measures?: MeasureField[];
  /** 多行维度（≥2 层时角区启用斜线表头，缺省仅 rowDim 单层）。 */
  rowDims?: DimField[];
  /** 多列维度（≥2 层时角区启用斜线表头，缺省仅 colDim 单层）。 */
  colDims?: DimField[];
  /** 角区斜线表头配置（缺省用文本 '交叉表'）。 */
  corner?: CornerConfig;
  title?: string;
  paper?: ReportIR['paper'];
}

export function buildCrosstab(input: CrosstabInput): ReportIR {
  const { datasetName, datasource, rowDim, colDim } = input;

  // 多层级维度：缺省回退到单层 rowDim / colDim。
  const rowDims: DimField[] = input.rowDims ?? [rowDim];
  const colDims: DimField[] = input.colDims ?? [colDim];

  // 度量：缺省空数组（无取值格）。
  const measures: MeasureField[] = input.measures ?? [];

  const rowDimCount = rowDims.length; // 行维度层级数（决定角区 rowspan）
  const colDimCount = colDims.length; // 列维度层级数（决定角区 colspan）
  const measureCount = measures.length; // 度量列数

  // 网格布局：
  //   角区占 rowDimCount 行 × colDimCount 列；列头从 colDimCount+1 列起；
  //   行头从 rowDimCount+1 行起；取值格在 (rowDimCount+1, colDimCount+1) 起，横向并排。
  const columnCount = colDimCount + measureCount; // 列头 + N 个度量列
  const rowCount = rowDimCount + 1; // 行头 + 数据行

  const cornerRowspan = rowDimCount;
  const cornerColspan = colDimCount;
  const headerRow = rowDimCount; // 列头所在行（= 角区行数）
  const headerCol = colDimCount; // 行头所在列（= 角区列数）
  const dataRow = rowDimCount + 1;
  const dataCol = colDimCount + 1;

  // 角区占用的列/行：以默认列宽/行高为基准（如需整体放大角区，覆盖 ReportIR.columns/rows 即可）。
  const DEFAULT_COL_WIDTH = 80;
  const DEFAULT_ROW_HEIGHT = 26;
  const columns: ReportIR['columns'] = [];
  for (let n = 1; n <= colDimCount; n++) {
    columns.push({ num: n, width: DEFAULT_COL_WIDTH });
  }
  const rows: ReportIR['rows'] = [];
  for (let n = 1; n <= rowDimCount; n++) {
    rows.push({ num: n, height: DEFAULT_ROW_HEIGHT });
  }

  const cells: CellIR[] = [];

  // 角区 A1（合并 rowDimCount 行 × colDimCount 列）：交叉表角区无条件使用 crosstab。
  // width/height/items 由序列化引擎按角区列宽之和、行高之和及维度标签自动计算，IR 不写。
  {
    const corner = input.corner ?? {};
    cells.push({
      row: 1,
      col: 1,
      rowspan: cornerRowspan,
      colspan: cornerColspan,
      content: {
        kind: 'crosstab',
        direction: corner.direction ?? 'down',
      },
      style: { align: 'center', valign: 'middle', bold: true, bgcolor: '#f2f2f2' },
    });
  }

  // 列头：每个列维度一层，向右扩展（expand=right，group 去重）。
  colDims.forEach((dim, i) => {
    cells.push({
      row: 1 + i,
      col: dataCol,
      content: { kind: 'dataset', dataset: datasetName, field: dim.name, aggregate: 'group' },
      expand: 'right',
      style: { align: 'center', bold: true },
    });
  });

  // 行头：每个行维度一层，向下扩展（expand=down，group 去重）。
  rowDims.forEach((dim, i) => {
    cells.push({
      row: dataRow,
      col: 1 + i,
      content: { kind: 'dataset', dataset: datasetName, field: dim.name, aggregate: 'group' },
      expand: 'down',
      style: { align: 'left', bold: true },
    });
  });

  // 取值格（交叉：top 指向列头，left 指向行头，dataset + sum 聚合，对齐官方）。
  // 列头单元格名：第 headerRow 行、第 dataCol 列；行头单元格名：第 dataRow 行、第 headerCol 列。
  const colHeaderName = cellName(dataCol, headerRow);
  const rowHeaderName = cellName(headerCol, dataRow);
  measures.forEach((m, i) => {
    cells.push({
      row: dataRow,
      col: dataCol + i,
      content: { kind: 'dataset', dataset: datasetName, field: m.name, aggregate: 'sum' },
      top: colHeaderName,
      left: rowHeaderName,
      style: { align: 'right' },
    });
  });

  // 数据集字段：所有行/列维度 + 所有度量。
  const dimFields = [...rowDims, ...colDims].map((d) => ({
    name: d.name,
    label: d.label ?? d.name,
    type: d.type,
  }));
  const dataset: DatasetIR = {
    name: datasetName,
    type: 'sql',
    datasource: datasource.name,
    readFieldRemark: false,
    fields: [
      ...dimFields,
      ...measures.map((m) => ({ name: m.name, label: m.label ?? m.name, type: m.type })),
    ],
  };

  return {
    name: input.name ?? '交叉表',
    rowCount,
    columnCount,
    cells,
    columns,
    rows,
    datasets: [dataset],
    datasources: [datasource],
    title: input.title ?? 'fileName()',
    paper: input.paper,
  };
}

/** 由 (col, row) 推导 A1 风格单元格名（col → 字母，row → 数字）。 */
function cellName(col: number, row: number): string {
  let n = col;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return `${s}${row}`;
}
